import { PointerEvent, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getVoiceValue, Voice, VoiceValues } from '../../../common/src/model.ts';
import eighthNoteIcon from '../assets/eighth-note.svg';
import { SliderBase, ThumbSize } from './player_util.tsx';
import './voice_slider.css';

export function VoiceSlider({
  voice,
  voiceValues,
  handleVoiceSliderChange,
  onLiveChange,
}: {
  voice: Voice;
  voiceValues: VoiceValues;
  // the value when the slider is let go: stored
  handleVoiceSliderChange: (voice: Voice, value: number) => void;
  // the value as the slider moves, for a player that can follow it
  onLiveChange?: (voice: Voice, value: number) => void;
}) {
  const value = getVoiceValue(voice, voiceValues);
  const inputRef = useRef<HTMLInputElement>(null);
  const thumbRef = useRef<HTMLInputElement>(null);

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLInputElement>) => {
      handleVoiceSliderChange(voice, Number(event.currentTarget.value));
    },
    [handleVoiceSliderChange, voice],
  );

  useEffect(() => {
    if (!inputRef.current || value === undefined) {
      return;
    }
    inputRef.current.value = value.toString();
    inputRef.current.style.setProperty('--thumb-value', `"Z"`);
  }, [inputRef, value]);

  const onInput = useCallback(() => {
    if (!inputRef.current) {
      return;
    }
    thumbRef.current?.style.setProperty('--val', inputRef.current.value);
    onLiveChange?.(voice, Number(inputRef.current.value));
  }, [onLiveChange, voice]);

  const isPiano = voice === Voice.Piano;
  const SliderWrapperClass = isPiano ? PianoSliderWrapper : VoiceSliderWrapper;

  return (
    <SliderWrapper>
      <SliderWrapperClass
        className={isPiano ? 'eighthNoteBackground' : ''}
        min={0}
        max={1}
        step={0.01}
        type="range"
        onPointerUp={onPointerUp}
        ref={inputRef}
        onInput={onInput}
      />
      {!isPiano && (
        <SliderThumb style={{ '--val': value } as any} $min={0} $max={1} ref={thumbRef}>
          <SliderThumbText>{voice[0].toUpperCase()}</SliderThumbText>
        </SliderThumb>
      )}
    </SliderWrapper>
  );
}

const SliderWrapper = styled.div`
  width: 100%;
  container-type: inline-size;
  display: flex;
  align-items: center;
  position: relative;
`;
const SliderThumb = styled.div<{ $min: number; $max: number }>`
  position: absolute;
  transform: translate(calc(var(--val) * (100cqw - ${ThumbSize})));
  border: 2px solid var(--color-slider-thumb-border);
  border-radius: 10px;
  color: var(--color-background);
  background: var(--color-slider-thumb);
  pointer-events: none;
  width: ${ThumbSize};
  height: ${ThumbSize};

  display: flex;
  justify-content: center;
  align-items: center;
`;
const SliderThumbText = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text-black);
`;

const VoiceSliderWrapper = styled(SliderBase)`
  &::-webkit-slider-thumb {
    -webkit-appearance: none; /* Reset default appearance */
    box-shadow: none;
    width: 65px;
    height: 65px;
    background: transparent;
    border-radius: 50%; /* Makes it a circle */
    cursor: pointer;
    transform: translateY(-20px);
  }
`;

const PianoSliderWrapper = styled(SliderBase)`
  &::-webkit-slider-thumb {
    /*box-shadow: 1px 1px 1px #000000, 0 0 1px #0d0d0d;*/
    border: 2px solid var(--color-slider-thumb-border);
    height: 48px;
    width: 48px;
    border-radius: 10px;
    cursor: pointer;
    -webkit-appearance: none;
    margin-top: -20px;
    background-size: 23px;
    background-repeat: no-repeat;
    background-position: center;
    background-image: url(${eighthNoteIcon});
    background-color: var(--color-slider-thumb);
  }
`;
